import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Badge,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
} from "reactstrap";
import { useHistory, RouteComponentProps } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-toastify";

import { BreadCrumb } from "../../components/Common";
import { PaginateQuery } from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import {
  MediaConversionStatusDto,
  MediaStatusEnum,
} from "../../interfaces/media";
import {
  AppTable,
  AppVideoPlayer,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../components";
import { MediaTypeEnum } from "../../enums";
import { RoleEnum } from "../../enums";
import {
  useGetMediaConversionStatusQuery,
  useSetMediaNeedsReconversionMutation,
  useSetMediaReprocessingPriorityMutation,
  useSkipMediaReprocessingMutation,
} from "../../store/api/mediaApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../store/api/subjectApi";
import { useGetChaptersSelectListBySubjectQuery } from "../../store/api/chapterApi";
import { useGetLessonSelectListByChapterQuery } from "../../store/api/lessonApi";
import { RequireSuperAdmin } from "../../routes/AuthProtected";
import { useSelector } from "react-redux";
import { AccountState } from "../../store/slices/accountSlice";
import { formatFileSize } from "../../services/helper.service";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { MediaEducationalPathModal } from "./MediaEducationalPathModal";

type TProps = RouteComponentProps & {};

type PathFilters = {
  educationSystemId: string;
  curriculumId: string;
  courseId: string;
  stageId: string;
  gradeId: string;
  subjectId: string;
  chapterId: string;
  lessonId: string;
};

const emptyPathFilters = (): PathFilters => ({
  educationSystemId: "",
  curriculumId: "",
  courseId: "",
  stageId: "",
  gradeId: "",
  subjectId: "",
  chapterId: "",
  lessonId: "",
});

const labelOf = (item: any, langKey: "en" | "ar") =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  item?.label ||
  "-";

const localizedText = (
  value: { en?: string; ar?: string } | undefined,
  langKey: "en" | "ar",
) => value?.[langKey] || value?.en || "";

const getConversionBadgeColor = (status?: MediaStatusEnum) => {
  switch (status) {
    case MediaStatusEnum.NEW:
      return "secondary";
    case MediaStatusEnum.PROCESSING:
      return "warning";
    case MediaStatusEnum.COMPLETED:
      return "success";
    case MediaStatusEnum.FAILED:
      return "danger";
    default:
      return "secondary";
  }
};

const getReprocessingLabelKey = (row: MediaConversionStatusDto) => {
  if (row.isReprocessing) return "reprocessingInProgress";
  if (row.reprocessingError) return "reprocessingFailed";
  if (row.reprocessingEndedAt) return "reprocessingCompleted";
  return "reprocessingNotStarted";
};

const getReprocessingBadgeColor = (row: MediaConversionStatusDto) => {
  if (row.isReprocessing) return "warning";
  if (row.reprocessingError) return "danger";
  if (row.reprocessingEndedAt) return "success";
  return "secondary";
};

/** Successfully marked complete (real or skipped) — no skip action needed. */
const isReprocessingSuccessfullyCompleted = (row: MediaConversionStatusDto) =>
  !!row.reprocessingEndedAt && !row.reprocessingError && !row.isReprocessing;

export const MediaConversionStatusPage: React.FC<TProps> = () => {
  return (
    <RequireSuperAdmin>
      <MediaConversionStatusPageContent />
    </RequireSuperAdmin>
  );
};

const MediaConversionStatusPageContent: React.FC = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const profile = useSelector(
    (state: { account: AccountState }) => state.account?.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const initState = new PaginateQuery();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>(MediaTypeEnum.VIDEO);
  const [reprocessingFilter, setReprocessingFilter] = useState<string>("");
  const [needsReconversionFilter, setNeedsReconversionFilter] =
    useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [pathFilters, setPathFilters] = useState<PathFilters>(emptyPathFilters);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);
  const [defaultSortFieldId, setDefaultSortFieldId] = useState(5);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [needsReconversionUpdatingId, setNeedsReconversionUpdatingId] =
    useState<string | null>(null);
  const [priorityUpdatingId, setPriorityUpdatingId] = useState<string | null>(
    null,
  );
  const [skipTarget, setSkipTarget] = useState<MediaConversionStatusDto | null>(
    null,
  );
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [detailMedia, setDetailMedia] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    pathFilters.educationSystemId || undefined,
    { skip: !pathFilters.educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId: pathFilters.curriculumId,
      courseId: pathFilters.courseId,
    };
    return q;
  }, [pathFilters.curriculumId, pathFilters.courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !pathFilters.curriculumId || !pathFilters.courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === pathFilters.stageId),
    [stages, pathFilters.stageId],
  );
  const grades = selectedStage?.grades || [];

  const { data: subjects } = useGetSubjectsSelectListByGradeQuery(
    pathFilters.gradeId,
    { skip: !pathFilters.gradeId },
  );
  const { data: chapters } = useGetChaptersSelectListBySubjectQuery(
    pathFilters.subjectId,
    { skip: !pathFilters.subjectId },
  );
  const { data: lessons } = useGetLessonSelectListByChapterQuery(
    pathFilters.chapterId,
    { skip: !pathFilters.chapterId },
  );

  const { data: queryData, isLoading: loading, refetch } =
    useGetMediaConversionStatusQuery(paginateQuery, {
      skip: !isSuperAdmin || paginateQuery === initState,
      pollingInterval: 15000,
    });
  const [setNeedsReconversion] = useSetMediaNeedsReconversionMutation();
  const [setReprocessingPriority] = useSetMediaReprocessingPriorityMutation();
  const [skipMediaReprocessing, { isLoading: skipLoading }] =
    useSkipMediaReprocessingMutation();
  const response = queryData;
  const rows = response?.data || [];
  const playingRow =
    playingIndex !== null ? rows[playingIndex] ?? null : null;

  document.title = `${t("documentTitle", {
    pageName: "mediaConversionStatus",
  })}`;

  const onCopyText = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(t("copied"));
      window.setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error(t("error"));
    }
  };

  const onToggleNeedsReconversion = async (row: MediaConversionStatusDto) => {
    const next = !row.needsReconversion;
    setNeedsReconversionUpdatingId(row.id);
    try {
      await setNeedsReconversion({
        id: row.id,
        needsReconversion: next,
      }).unwrap();
      toast.success(
        next
          ? t("needsReconversionEnabled", {
              defaultValue: "Queued for reconversion",
            })
          : t("needsReconversionDisabled", {
              defaultValue: "Removed from reconversion queue",
            }),
      );
    } catch {
      toast.error(t("error"));
    } finally {
      setNeedsReconversionUpdatingId(null);
    }
  };

  const onTogglePriority = async (row: MediaConversionStatusDto) => {
    const next = !row.reprocessingPriority;
    setPriorityUpdatingId(row.id);
    try {
      await setReprocessingPriority({
        id: row.id,
        priority: next,
      }).unwrap();
      toast.success(
        next
          ? t("reprocessingPriorityEnabled", {
              defaultValue: "Priority enabled — will reconvert first",
            })
          : t("reprocessingPriorityDisabled", {
              defaultValue: "Priority cleared",
            }),
      );
    } catch {
      toast.error(t("error"));
    } finally {
      setPriorityUpdatingId(null);
    }
  };

  const onConfirmSkipReprocessing = async () => {
    if (!skipTarget) return;
    try {
      await skipMediaReprocessing(skipTarget.id).unwrap();
      toast.success(
        t("reprocessingSkipped", {
          defaultValue: "Marked as reprocessing completed (skipped)",
        }),
      );
      setSkipTarget(null);
    } catch {
      toast.error(t("error"));
    }
  };

  const columns: TableColumn<MediaConversionStatusDto>[] = [
    {
      name: <ColumnHeader title="id" />,
      cell: (row) => {
        const copyKey = `id:${row.id}`;
        const isCopied = copiedKey === copyKey;
        return (
          <div className="d-flex align-items-center gap-1 py-1" style={{ minWidth: 0 }}>
            <code
              className="text-truncate mb-0"
              style={{ fontSize: "11px", maxWidth: "140px" }}
              title={row.id}
            >
              {row.id}
            </code>
            <button
              type="button"
              className="btn btn-sm btn-ghost-secondary px-1"
              onClick={() => onCopyText(copyKey, row.id)}
              title={t("copy")}
            >
              <i
                className={
                  isCopied ? "ri-check-line text-success" : "ri-file-copy-line"
                }
              />
            </button>
          </div>
        );
      },
      sortable: false,
      width: "200px",
    },
    {
      name: <ColumnHeader title="lecture" />,
      cell: (row) => {
        const title =
          localizedText(row.lectureName, langKey) || t("noLecture", {
            defaultValue: "—",
          });
        const description = localizedText(row.lectureDescription, langKey);
        return (
          <div className="py-1" style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" title={title}>
              {title}
            </div>
            {description ? (
              <div
                className="text-muted fs-12 text-truncate"
                style={{ maxWidth: "280px" }}
                title={description.replace(/<[^>]+>/g, "")}
              >
                {description.replace(/<[^>]+>/g, " ").trim()}
              </div>
            ) : null}
          </div>
        );
      },
      sortable: false,
      minWidth: "220px",
    },
    {
      name: <ColumnHeader title="videoUrl" />,
      cell: (row) => {
        if (!row.videoUrl) return <TextColumn text="-" />;
        const copyKey = `url:${row.id}`;
        const isCopied = copiedKey === copyKey;
        return (
          <div className="d-flex align-items-center gap-1 py-1" style={{ minWidth: 0 }}>
            <a
              href={row.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="text-truncate"
              style={{ fontSize: "11px", maxWidth: "240px" }}
              title={row.videoUrl}
            >
              {row.videoUrl}
            </a>
            <button
              type="button"
              className="btn btn-sm btn-ghost-secondary px-1"
              onClick={() => onCopyText(copyKey, row.videoUrl!)}
              title={t("copy")}
            >
              <i
                className={
                  isCopied ? "ri-check-line text-success" : "ri-file-copy-line"
                }
              />
            </button>
          </div>
        );
      },
      sortable: false,
      minWidth: "280px",
    },
    {
      name: <ColumnHeader title="type" />,
      cell: (row) => <TextColumn text={t(row.type)} />,
      sortable: true,
      id: 2,
      width: "110px",
    },
    {
      name: <ColumnHeader title="size" />,
      cell: (row) => (
        <TextColumn text={formatFileSize(Number(row.size) || 0)} />
      ),
      sortable: true,
      id: 6,
      width: "110px",
    },
    {
      name: <ColumnHeader title="conversionStatus" />,
      cell: (row) => (
        <div className="d-flex align-items-center gap-1 flex-wrap py-1">
          <Badge color={getConversionBadgeColor(row.status)}>
            {t(row.status || MediaStatusEnum.NEW)}
          </Badge>
          {row.status === MediaStatusEnum.PROCESSING && (
            <Spinner size="sm" color="warning" />
          )}
        </div>
      ),
      sortable: true,
      id: 3,
      width: "150px",
    },
    {
      name: <ColumnHeader title="reprocessingState" />,
      cell: (row) => (
        <div className="d-flex flex-column gap-1 py-1">
          <Badge color={getReprocessingBadgeColor(row)}>
            {t(getReprocessingLabelKey(row))}
          </Badge>
          {row.reprocessingInstanceId && (
            <small className="text-muted">{row.reprocessingInstanceId}</small>
          )}
        </div>
      ),
      sortable: false,
      width: "160px",
    },
    {
      name: <ColumnHeader title="needsReconversion" />,
      cell: (row) => {
        const updating = needsReconversionUpdatingId === row.id;
        return (
          <Button
            color={row.needsReconversion ? "info" : "soft-secondary"}
            size="sm"
            disabled={updating || row.isReprocessing}
            onClick={() => onToggleNeedsReconversion(row)}
            title={
              row.needsReconversion
                ? t("clearNeedsReconversion", {
                    defaultValue: "Remove from reconversion queue",
                  })
                : t("setNeedsReconversion", {
                    defaultValue: "Queue for reconversion",
                  })
            }
          >
            {updating ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i
                  className={`${
                    row.needsReconversion
                      ? "ri-refresh-fill"
                      : "ri-refresh-line"
                  } me-1 align-bottom`}
                />
                {row.needsReconversion
                  ? t("queued", { defaultValue: "Queued" })
                  : t("notQueued", { defaultValue: "Not queued" })}
              </>
            )}
          </Button>
        );
      },
      sortable: true,
      id: 8,
      width: "150px",
    },
    {
      name: <ColumnHeader title="reprocessingPriority" />,
      cell: (row) => {
        const updating = priorityUpdatingId === row.id;
        return (
          <Button
            color={row.reprocessingPriority ? "warning" : "soft-secondary"}
            size="sm"
            disabled={updating || row.isReprocessing}
            onClick={() => onTogglePriority(row)}
            title={
              row.reprocessingPriority
                ? t("clearReprocessingPriority", {
                    defaultValue: "Clear priority",
                  })
                : t("setReprocessingPriority", {
                    defaultValue: "Set priority (reconvert first)",
                  })
            }
          >
            {updating ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i
                  className={`${
                    row.reprocessingPriority
                      ? "ri-flag-fill"
                      : "ri-flag-line"
                  } me-1 align-bottom`}
                />
                {row.reprocessingPriority
                  ? t("priorityOn", { defaultValue: "Priority" })
                  : t("priorityOff", { defaultValue: "Normal" })}
              </>
            )}
          </Button>
        );
      },
      sortable: true,
      id: 7,
      width: "140px",
    },
    {
      name: <ColumnHeader title="skipReprocessing" />,
      cell: (row) => {
        if (
          isReprocessingSuccessfullyCompleted(row) ||
          (!row.needsReconversion && !row.isReprocessing)
        ) {
          return <TextColumn text="-" />;
        }
        return (
          <Button
            color="soft-success"
            size="sm"
            onClick={() => setSkipTarget(row)}
            title={t("skipReprocessingTitle", {
              defaultValue:
                "Mark reprocessing completed without reconverting",
            })}
          >
            <i className="ri-check-double-line me-1 align-bottom" />
            {t("skipReprocessing", { defaultValue: "Skip" })}
          </Button>
        );
      },
      sortable: false,
      width: "120px",
    },
    {
      name: <ColumnHeader title="reprocessingStartedAt" />,
      cell: (row) =>
        row.reprocessingStartedAt ? (
          <DateColumn date={row.reprocessingStartedAt as Date} />
        ) : (
          <TextColumn text="-" />
        ),
      sortable: true,
      id: 4,
      width: "170px",
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt as Date} />,
      sortable: true,
      id: 5,
      width: "170px",
    },
    {
      name: <ColumnHeader title="processingError" />,
      cell: (row) => (
        <TextColumn
          text={row.processingError || row.reprocessingError || "-"}
        />
      ),
      sortable: false,
      minWidth: "200px",
    },
    {
      name: <ColumnHeader title="viewDetail" />,
      cell: (row) => (
        <div className="d-flex flex-column gap-1 py-1">
          <Button
            color="soft-primary"
            size="sm"
            disabled={!row.videoUrl}
            onClick={() => {
              const index = rows.findIndex((r) => r.id === row.id);
              setPlayingIndex(index >= 0 ? index : null);
            }}
          >
            <i className="ri-play-circle-line me-1 align-bottom" />
            {t("play")}
          </Button>
          <Button
            color="soft-secondary"
            size="sm"
            onClick={() =>
              setDetailMedia({
                id: row.id,
                name:
                  localizedText(row.lectureName, langKey) || row.name,
              })
            }
          >
            <i className="ri-eye-line me-1 align-bottom" />
            {t("viewDetail")}
          </Button>
        </div>
      ),
      sortable: false,
      width: "140px",
    },
  ];

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    switch (columnId) {
      case 1:
        return `name:${sortDirection.toUpperCase()}`;
      case 2:
        return `type:${sortDirection.toUpperCase()}`;
      case 3:
        return `status:${sortDirection.toUpperCase()}`;
      case 4:
        return `reprocessing_started_at:${sortDirection.toUpperCase()}`;
      case 6:
        return `size:${sortDirection.toUpperCase()}`;
      case 7:
        return `reprocessing_priority:${sortDirection.toUpperCase()}`;
      case 8:
        return `needs_reconversion:${sortDirection.toUpperCase()}`;
      case 5:
      default:
        return `created_at:${sortDirection.toUpperCase()}`;
    }
  };

  const buildFilter = (
    status: string,
    type: string,
    reprocessingState: string,
    needsReconversion: string,
    reprocessingPriority: string,
    path: PathFilters,
  ) => {
    const filter: Record<string, string> = {};
    if (status) filter.status = status;
    if (type) filter.type = type;
    if (reprocessingState) filter.reprocessingState = reprocessingState;
    if (needsReconversion) {
      filter.needsReconversion = needsReconversion;
    }
    if (reprocessingPriority) {
      filter.reprocessingPriority = reprocessingPriority;
    }
    if (path.educationSystemId) {
      filter.educationSystemId = path.educationSystemId;
    }
    if (path.curriculumId) filter.curriculumId = path.curriculumId;
    if (path.courseId) filter.courseId = path.courseId;
    if (path.stageId) filter.stageId = path.stageId;
    if (path.gradeId) filter.gradeId = path.gradeId;
    if (path.subjectId) filter.subjectId = path.subjectId;
    if (path.chapterId) filter.chapterId = path.chapterId;
    if (path.lessonId) filter.lessonId = path.lessonId;
    return filter;
  };

  const applyFilters = (
    next: Partial<{
      status: string;
      type: string;
      reprocessing: string;
      needsReconversion: string;
      priority: string;
      path: PathFilters;
    }>,
  ) => {
    const status = next.status ?? statusFilter;
    const type = next.type ?? typeFilter;
    const reprocessing = next.reprocessing ?? reprocessingFilter;
    const needsReconversion =
      next.needsReconversion ?? needsReconversionFilter;
    const priority = next.priority ?? priorityFilter;
    const path = next.path ?? pathFilters;

    if (next.status !== undefined) setStatusFilter(next.status);
    if (next.type !== undefined) setTypeFilter(next.type);
    if (next.reprocessing !== undefined) setReprocessingFilter(next.reprocessing);
    if (next.needsReconversion !== undefined) {
      setNeedsReconversionFilter(next.needsReconversion);
    }
    if (next.priority !== undefined) setPriorityFilter(next.priority);
    if (next.path !== undefined) setPathFilters(next.path);

    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: buildFilter(
        status,
        type,
        reprocessing,
        needsReconversion,
        priority,
        path,
      ),
    });
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, page: 1, search: searchText });
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<MediaConversionStatusDto>,
    sortDirection: string,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      sortBy: getSortByColumnId(column.id as number, sortDirection),
    });
  };

  const onChangeEducationSystem = (value: string) => {
    applyFilters({
      path: {
        educationSystemId: value,
        curriculumId: "",
        courseId: pathFilters.courseId,
        stageId: "",
        gradeId: "",
        subjectId: "",
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeCurriculum = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        curriculumId: value,
        stageId: "",
        gradeId: "",
        subjectId: "",
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeCourse = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        courseId: value,
        stageId: "",
        gradeId: "",
        subjectId: "",
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeStage = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        stageId: value,
        gradeId: "",
        subjectId: "",
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeGrade = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        gradeId: value,
        subjectId: "",
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeSubject = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        subjectId: value,
        chapterId: "",
        lessonId: "",
      },
    });
  };

  const onChangeChapter = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        chapterId: value,
        lessonId: "",
      },
    });
  };

  const onChangeLesson = (value: string) => {
    applyFilters({
      path: {
        ...pathFilters,
        lessonId: value,
      },
    });
  };

  const onClearPathFilters = () => {
    applyFilters({ path: emptyPathFilters() });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const status = queryParams.get("filter[status]") || "";
    const type = queryParams.get("filter[type]") || MediaTypeEnum.VIDEO;
    const reprocessingState = queryParams.get("filter[reprocessingState]") || "";
    const needsReconversion =
      queryParams.get("filter[needsReconversion]") || "";
    const reprocessingPriority =
      queryParams.get("filter[reprocessingPriority]") || "";
    const path: PathFilters = {
      educationSystemId: queryParams.get("filter[educationSystemId]") || "",
      curriculumId: queryParams.get("filter[curriculumId]") || "",
      courseId: queryParams.get("filter[courseId]") || "",
      stageId: queryParams.get("filter[stageId]") || "",
      gradeId: queryParams.get("filter[gradeId]") || "",
      subjectId: queryParams.get("filter[subjectId]") || "",
      chapterId: queryParams.get("filter[chapterId]") || "",
      lessonId: queryParams.get("filter[lessonId]") || "",
    };

    setStatusFilter(status);
    setTypeFilter(type);
    setReprocessingFilter(reprocessingState);
    setNeedsReconversionFilter(needsReconversion);
    setPriorityFilter(reprocessingPriority);
    setPathFilters(path);

    const query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : initState.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : initState.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : "created_at:DESC",
      search: queryParams.get("search") || "",
      filter: buildFilter(
        status,
        type,
        reprocessingState,
        needsReconversion,
        reprocessingPriority,
        path,
      ),
    };

    if (query.sortBy?.includes(":")) {
      setDefaultSortAsc(query.sortBy.split(":")[1] === "ASC");
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery === initState) return;

    const { limit, page, search, sortBy, filter } = paginateQuery;
    let query = `?page=${page || 1}`;
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    if (filter && Object.keys(filter).length > 0) {
      Object.keys(filter).forEach((key: string) => {
        if (filter[key]) query += `&filter[${key}]=${filter[key]}`;
      });
    }
    history.replace({ search: query });
  }, [paginateQuery]);

  // Keep player index valid when the page data refreshes / filters change
  useEffect(() => {
    if (playingIndex === null) return;
    if (!rows.length) {
      setPlayingIndex(null);
      return;
    }
    if (playingIndex >= rows.length) {
      setPlayingIndex(rows.length - 1);
    }
  }, [rows, playingIndex]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("mediaConversionStatus")}
            pageTitle={t("monitoring")}
          />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={6} md={3}>
                      <Card className="border shadow-none mb-0">
                        <CardBody className="py-3">
                          <div className="text-muted fs-12 mb-1">
                            {t("filteredTotalSize")}
                          </div>
                          <div className="fs-5 fw-semibold">
                            {formatFileSize(response?.summary?.totalSize || 0)}
                          </div>
                          <small className="text-muted">
                            {response?.summary?.totalCount ?? 0} {t("items")}
                          </small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col sm={6} md={3}>
                      <Card className="border shadow-none mb-0">
                        <CardBody className="py-3">
                          <div className="text-muted fs-12 mb-1">
                            {t("convertedSize")}
                          </div>
                          <div className="fs-5 fw-semibold text-success">
                            {formatFileSize(
                              response?.summary?.convertedSize || 0,
                            )}
                          </div>
                          <small className="text-muted">
                            {response?.summary?.convertedCount ?? 0} {t("items")}
                          </small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col sm={6} md={3}>
                      <Card className="border shadow-none mb-0">
                        <CardBody className="py-3">
                          <div className="text-muted fs-12 mb-1">
                            {t("remainingSize")}
                          </div>
                          <div className="fs-5 fw-semibold text-warning">
                            {formatFileSize(
                              response?.summary?.remainingSize || 0,
                            )}
                          </div>
                          <small className="text-muted">
                            {response?.summary?.remainingCount ?? 0} {t("items")}
                          </small>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col sm={3}>
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={3}>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          applyFilters({ status: event.target.value })
                        }
                      >
                        <option value="">{t("filterByStatus")} — {t("all")}</option>
                        {Object.values(MediaStatusEnum).map((status) => (
                          <option key={status} value={status}>
                            {t(status)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Input
                        type="select"
                        value={typeFilter}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          applyFilters({ type: event.target.value })
                        }
                      >
                        <option value="">{t("filterByType")} — {t("all")}</option>
                        {Object.values(MediaTypeEnum).map((type) => (
                          <option key={type} value={type}>
                            {t(type)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Input
                        type="select"
                        value={reprocessingFilter}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          applyFilters({ reprocessing: event.target.value })
                        }
                      >
                        <option value="">
                          {t("filterByReprocessing")} — {t("all")}
                        </option>
                        <option value="not_started">
                          {t("reprocessingNotStarted")}
                        </option>
                        <option value="in_progress">
                          {t("reprocessingInProgress")}
                        </option>
                        <option value="completed">
                          {t("reprocessingCompleted")}
                        </option>
                        <option value="failed">
                          {t("reprocessingFailed")}
                        </option>
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Input
                        type="select"
                        value={needsReconversionFilter}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          applyFilters({
                            needsReconversion: event.target.value,
                          })
                        }
                      >
                        <option value="">
                          {t("filterByNeedsReconversion", {
                            defaultValue: "Needs reconversion",
                          })}{" "}
                          — {t("all")}
                        </option>
                        <option value="true">
                          {t("queued", { defaultValue: "Queued" })}
                        </option>
                        <option value="false">
                          {t("notQueued", { defaultValue: "Not queued" })}
                        </option>
                      </Input>
                    </Col>
                    <Col sm={2}>
                      <Input
                        type="select"
                        value={priorityFilter}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          applyFilters({ priority: event.target.value })
                        }
                      >
                        <option value="">
                          {t("filterByPriority", {
                            defaultValue: "Priority",
                          })}{" "}
                          — {t("all")}
                        </option>
                        <option value="true">
                          {t("priorityOn", { defaultValue: "Priority" })}
                        </option>
                        <option value="false">
                          {t("priorityOff", { defaultValue: "Normal" })}
                        </option>
                      </Input>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <Button color="success" onClick={() => refetch()}>
                        <i className="ri-refresh-line me-1 align-bottom"></i>
                        {t("refresh")}
                      </Button>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3 align-items-end">
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("educationSystem")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.educationSystemId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeEducationSystem(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(educationSystems || []).map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {labelOf(s, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("curriculum")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.curriculumId}
                        disabled={!pathFilters.educationSystemId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeCurriculum(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(curricula || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {labelOf(c, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("course")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.courseId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeCourse(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(courses || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {labelOf(c, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("educationalStage")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.stageId}
                        disabled={
                          !pathFilters.curriculumId || !pathFilters.courseId
                        }
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeStage(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {stages.map((stage) => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name?.[langKey] || stage.name?.en || "-"}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("grade")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.gradeId}
                        disabled={!pathFilters.stageId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeGrade(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {grades.map((grade) => (
                          <option key={grade.id} value={grade.id}>
                            {grade.name?.[langKey] || grade.name?.en || "-"}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("subject")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.subjectId}
                        disabled={!pathFilters.gradeId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeSubject(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(subjects || []).map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {labelOf(s, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("chapter")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.chapterId}
                        disabled={!pathFilters.subjectId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeChapter(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(chapters || []).map((c: any) => (
                          <option key={c.id} value={c.id}>
                            {labelOf(c, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={6} md={4} lg={2}>
                      <Label className="form-label mb-1 fs-12 text-muted">
                        {t("lesson")}
                      </Label>
                      <Input
                        type="select"
                        value={pathFilters.lessonId}
                        disabled={!pathFilters.chapterId}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onChangeLesson(event.target.value)
                        }
                      >
                        <option value="">{t("all")}</option>
                        {(lessons || []).map((l: any) => (
                          <option key={l.id} value={l.id}>
                            {labelOf(l, langKey)}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col className="col-sm-auto">
                      <Button
                        color="soft-secondary"
                        onClick={onClearPathFilters}
                        disabled={
                          !pathFilters.educationSystemId &&
                          !pathFilters.curriculumId &&
                          !pathFilters.courseId &&
                          !pathFilters.stageId &&
                          !pathFilters.gradeId &&
                          !pathFilters.subjectId &&
                          !pathFilters.chapterId &&
                          !pathFilters.lessonId
                        }
                      >
                        <i className="ri-filter-off-line me-1 align-bottom" />
                        {t("clearFilters", { defaultValue: "Clear path" })}
                      </Button>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme="dark"
                        columns={columns}
                        data={rows}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Modal
        isOpen={playingIndex !== null && !!playingRow}
        toggle={() => setPlayingIndex(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setPlayingIndex(null)}>
          {localizedText(playingRow?.lectureName, langKey) ||
            playingRow?.name ||
            t("previewVideo")}
          {playingIndex !== null && rows.length > 0 ? (
            <span className="text-muted fs-13 ms-2">
              ({playingIndex + 1}/{rows.length})
            </span>
          ) : null}
        </ModalHeader>
        <ModalBody>
          {playingRow?.videoUrl ? (
            <div className="player-wrapper mb-3">
              <AppVideoPlayer
                key={playingRow.id}
                src={playingRow.videoUrl}
                title={
                  localizedText(playingRow.lectureName, langKey) ||
                  playingRow.name
                }
              />
            </div>
          ) : (
            <p className="text-muted">{t("noVideo", { defaultValue: "No video URL" })}</p>
          )}
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
            <Button
              color="soft-secondary"
              disabled={playingIndex === null || playingIndex <= 0}
              onClick={() =>
                setPlayingIndex((prev) =>
                  prev !== null && prev > 0 ? prev - 1 : prev,
                )
              }
            >
              <i className="ri-arrow-left-line me-1 align-bottom" />
              {t("previous")}
            </Button>
            <div className="d-flex gap-2">
              {playingRow?.videoUrl ? (
                <Button
                  color="soft-primary"
                  tag="a"
                  href={playingRow.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="ri-external-link-line me-1 align-bottom" />
                  {t("open")}
                </Button>
              ) : null}
              <Button color="light" onClick={() => setPlayingIndex(null)}>
                {t("close")}
              </Button>
            </div>
            <Button
              color="soft-secondary"
              disabled={
                playingIndex === null || playingIndex >= rows.length - 1
              }
              onClick={() =>
                setPlayingIndex((prev) =>
                  prev !== null && prev < rows.length - 1 ? prev + 1 : prev,
                )
              }
            >
              {t("next")}
              <i className="ri-arrow-right-line ms-1 align-bottom" />
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <ConfirmationModal
        show={!!skipTarget}
        title={t("areYouSure")}
        description={t("skipReprocessingConfirmDescription", {
          defaultValue:
            "Mark \"{name}\" as reprocessing completed without reconverting? The converter will not pick it up.",
          name: skipTarget?.name,
        })}
        confirmationButtonTitle={t("skipReprocessing", {
          defaultValue: "Skip",
        })}
        confirmationButtonInProgressTitle={t("saving")}
        loading={skipLoading}
        icon={<></>}
        onCloseClick={() => {
          if (!skipLoading) setSkipTarget(null);
        }}
        onConfirmClick={onConfirmSkipReprocessing}
      />
      <MediaEducationalPathModal
        show={!!detailMedia}
        mediaId={detailMedia?.id}
        mediaName={detailMedia?.name}
        onCloseClick={() => setDetailMedia(null)}
      />
    </React.Fragment>
  );
};
